import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { Utensils, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role) {
      if (user.role === 'employee') navigate('/employee/badge');
      else navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === 'employee') navigate('/employee/badge');
      else navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#F8F1DE_0%,_#FAF8F5_42%,_#F3F0E8_100%)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Utensils className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#2A2A28]">Le Traiteur Libanais</h1>
          <p className="text-sm text-[#7A7A75] mt-1">Espace Administration</p>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#7A7A75]">
            Connectez-vous pour gerer les commandes, reservations, employes et parametres du restaurant.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-[28px] border border-[#EBE8E0] p-6 sm:p-8 shadow-[0_18px_50px_rgba(42,42,40,0.08)]" data-testid="login-form">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-[#2A2A28]">Email</Label>
              <Input type="email" className="mt-1.5 rounded-xl border-[#EBE8E0] focus:ring-[#5B6B3F]/20 focus:border-[#5B6B3F]" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2A2A28]">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? 'text' : 'password'} className="rounded-xl border-[#EBE8E0] pr-10 focus:ring-[#5B6B3F]/20 focus:border-[#5B6B3F]" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A7A75] hover:text-[#5B6B3F] transition-colors" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-6 rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white h-11 font-medium" data-testid="login-submit">
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-[#7A7A75] hover:text-[#5B6B3F] transition-colors">Retour au site</a>
        </div>
      </div>
    </div>
  );
}
